import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Aspect,
  MagicRef,
  MagicResult,
  MagicTemplate,
  ModelSelection,
  refImageUrl,
  refName,
} from '../model';
import type { AiApiKey, CreateAiApiKeyPayload } from '../aiApi';
import type { LocalOpState } from '../localOps/types';
import { useImageSize } from '../useImageMeta';
import { MAGIC_BACKEND_ENABLED } from '../providers/config';
import { suggestPrompts } from '../promptHistory';
import { TM } from '../tokens';
import { ExternalLinkIcon, HistoryIcon, KeyIcon, MagicIcon, WarningIcon } from '../icons';
import { AssetThumb } from './AssetThumb';
import { ResultViewer } from './ResultViewer';
import { PromptSuggest, SUGGEST_HOTKEY_LABEL } from './PromptSuggest';
import { HoverPopover, MagicBtn } from './ui';
import { ApiKeyControl } from './ApiKeyControl';
import { AiKeysControl } from './AiKeysControl';
import { LegacyModelPicker, ModelPicker } from './ModelPicker';

// Prompt textarea auto-grows with content, from 2 rows up to this many before
// it starts scrolling internally. Line height is fixed in styles.scss so the
// pixel maths below stays predictable.
const PROMPT_LINE_HEIGHT = 20;
const PROMPT_VERTICAL_PADDING = 20; // 10px top + 10px bottom (see .al-magic-prompt)
const PROMPT_MIN_ROWS = 2;
const PROMPT_MAX_ROWS = 10;

// The preview shown before generating is the session's primary reference —
// whatever its source: an Asset Lab asset, an upload / clipboard paste, or a
// frame captured from the fullscreen viewer.
const EmptyCanvas: React.FC<{ primaryRef: MagicRef | null; tpl?: MagicTemplate }> = ({
  primaryRef,
  tpl,
}) => {
  const refUrl = primaryRef ? refImageUrl(primaryRef) : null;
  const refSize = useImageSize(refUrl);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        color: TM.textMute,
        textAlign: 'center',
        maxWidth: 380,
      }}
    >
      {primaryRef && (
        <div
          style={{
            padding: 8,
            borderRadius: 10,
            background: '#fff',
            border: `1px solid ${TM.border}`,
            boxShadow: TM.shadow,
          }}
        >
          <AssetThumb src={refUrl} name={refName(primaryRef)} size={312} />
        </div>
      )}
      {primaryRef && (
        <div style={{ fontSize: 11, color: TM.textFaint, marginTop: -6 }}>
          Reference: <b style={{ color: TM.textMute }}>{refName(primaryRef)}</b>
          {refSize && (
            <>
              {' · '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {refSize.w} × {refSize.h}
              </span>
            </>
          )}
        </div>
      )}
      <div style={{ fontSize: 14, fontWeight: 600, color: TM.text }}>Ready to generate</div>
      <div style={{ fontSize: 12, lineHeight: 1.5 }}>
        Template: <b style={{ color: TM.text }}>{tpl?.label || '—'}</b>
        <br />
        {tpl?.hint} Adjust the prompt and hit Generate.
      </div>
    </div>
  );
};

// Strips markdown link syntax "[label](url)" down to "label" so server
// messages (e.g. Gemini's "…[send feedback](https://…)") read cleanly as text.
const plainText = (msg: string) => msg.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '$1');

// Shown in the preview area when a generation fails — surfaces the provider /
// model message verbatim (e.g. Gemini's finishMessage) instead of a transient
// toast, so the user can read and act on it.
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      textAlign: 'center',
      maxWidth: 440,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDECEA',
        color: TM.orange,
      }}
    >
      <WarningIcon size={22} />
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: TM.text }}>Generation failed</div>
    <div style={{ fontSize: 12, lineHeight: 1.5, color: TM.textMute, whiteSpace: 'pre-wrap' }}>
      {plainText(message)}
    </div>
  </div>
);

// Survey form gathering interest in full, server-side corporate access — opened
// from the "coming soon" screen's primary action (see ComingSoonState).
const CORPORATE_ACCESS_FORM_URL = 'https://forms.gle/WC5TsJzgq8Vb6eP56';

// Corporate access is only on offer on our own deployments — the
// "I don't have an API key" button is hidden everywhere else.
const CORPORATE_ACCESS_HOSTS = ['adsadvisor.my.games', 'preprod.adsadvisor.io'];
const showCorporateAccess = () => CORPORATE_ACCESS_HOSTS.includes(window.location.hostname);

// Shown when the list of keys could not be loaded at all (403 without Asset Lab
// access, 5xx). Unlike an empty list this is *not* "you have no keys yet" — we
// simply don't know — so the canvas offers a retry instead of the add form.
const KeysErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      textAlign: 'center',
      maxWidth: 440,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDECEA',
        color: TM.orange,
      }}
    >
      <WarningIcon size={22} />
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: TM.text }}>
      Could not load your API keys
    </div>
    <div style={{ fontSize: 12, lineHeight: 1.5, color: TM.textMute }}>{message}</div>
    <MagicBtn kind="secondary" onClick={onRetry}>
      Try again
    </MagicBtn>
  </div>
);

// Shown in place of the canvas when the user hits Generate with no key to
// generate with. `onConnectApiKey` opens the same key popover the prompt-toolbar
// control uses, so the screen is a way forward rather than a dead end.
//
// The wording differs per path: on the backend path generation is live and the
// only thing missing is the user's own provider key registered on our server; on
// the legacy direct path server-side generation genuinely wasn't available yet.
const ComingSoonState: React.FC<{ onConnectApiKey: () => void }> = ({ onConnectApiKey }) => {
  const corporateAccess = showCorporateAccess();
  return MAGIC_BACKEND_ENABLED ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
        textAlign: 'center',
        maxWidth: 520,
        padding: '0 24px',
      }}
    >
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: TM.magicGradient,
          color: '#fff',
          boxShadow: TM.shadow,
        }}
      >
        <KeyIcon size={40} />
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: TM.text, lineHeight: 1.2 }}>
        Connect an API key to start generating
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          fontSize: 15,
          lineHeight: 1.55,
          color: TM.textMute,
        }}
      >
        <div>
          Add your Gemini or OpenAI (Image GPT) API key and pick a model to generate. The key is
          stored encrypted on our server and all provider calls are made from our backend — you set
          a monthly spending cap when you add it.
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 12,
          marginTop: 2,
        }}
      >
        {corporateAccess && (
          <MagicBtn
            kind="secondary"
            icon={<ExternalLinkIcon size={14} />}
            onClick={() => window.open(CORPORATE_ACCESS_FORM_URL, '_blank', 'noopener,noreferrer')}
          >
            I don&apos;t have an API key
          </MagicBtn>
        )}
        <MagicBtn kind="magic" icon={<KeyIcon size={14} />} onClick={onConnectApiKey}>
          Connect API Key
        </MagicBtn>
      </div>
    </div>
  ) : (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
        textAlign: 'center',
        maxWidth: 520,
        padding: '0 24px',
      }}
    >
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: TM.magicGradient,
          color: '#fff',
          boxShadow: TM.shadow,
        }}
      >
        <MagicIcon size={40} />
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: TM.text, lineHeight: 1.2 }}>
        Advanced generation is in development
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          fontSize: 15,
          lineHeight: 1.55,
          color: TM.textMute,
        }}
      >
        <div>
          We are still putting the finishing touches on our server-side models. Right now, you can
          connect your own Gemini (Nana Banana) API key to unlock full generation, or use Remove BG
          and Upscale features locally for free.
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 12,
          marginTop: 2,
        }}
      >
        {corporateAccess && (
          <MagicBtn
            kind="primary"
            icon={<ExternalLinkIcon size={14} />}
            onClick={() => window.open(CORPORATE_ACCESS_FORM_URL, '_blank', 'noopener,noreferrer')}
          >
            I don&apos;t have an API key
          </MagicBtn>
        )}
        <MagicBtn kind="secondary" icon={<KeyIcon size={14} />} onClick={onConnectApiKey}>
          Connect API Key
        </MagicBtn>
      </div>
    </div>
  );
};

const GeneratingState: React.FC<{ aspect: Aspect }> = ({ aspect }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      color: TM.textMute,
    }}
  >
    <div
      className="al-magic-generating-tile"
      style={{ width: 156, height: 156, background: TM.magicGradient }}
    >
      <div className="al-magic-shimmer" />
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: TM.text }}>Generating · {aspect}</div>
    <div style={{ fontSize: 11, color: TM.textFaint }}>Sending refs + prompt to the model…</div>
  </div>
);

// Shown while an on-device tool runs (background removal / upscale). Reuses the
// generating tile but reads the download / compute phase, so the first-run
// model download surfaces a percentage.
const ProcessingState: React.FC<{ localOp: LocalOpState }> = ({ localOp }) => {
  const pct = localOp.progress == null ? null : Math.round(localOp.progress * 100);
  const title = localOp.kind === 'removeBg' ? 'Removing background' : 'Upscaling';
  const sub =
    localOp.phase === 'downloading'
      ? `Downloading model${pct == null ? '…' : ` · ${pct}%`}`
      : localOp.phase === 'loading'
        ? // Already cached — reading the model into memory, not re-downloading.
          `Loading model${pct == null ? '…' : ` · ${pct}%`}`
        : pct == null
          ? 'Running on your device…'
          : `Running on your device · ${pct}%`;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        color: TM.textMute,
      }}
    >
      <div
        className="al-magic-generating-tile"
        style={{ width: 156, height: 156, background: TM.magicGradient }}
      >
        <div className="al-magic-shimmer" />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: TM.text }}>{title}</div>
      <div style={{ fontSize: 11, color: TM.textFaint }}>{sub}</div>
    </div>
  );
};

// The template chip shown next to "Prompt": names the active mode and, on
// hover, reveals its system instruction in a popover. The popover's action
// pulls that instruction into the prompt field and switches back to "No
// template", so the user can tweak it as plain content. It also lists any
// after-generation post-processing the template applies.
const TemplateBadge: React.FC<{ tpl: MagicTemplate; onResetToCustom: () => void }> = ({
  tpl,
  onResetToCustom,
}) => {
  const hasSystem = tpl.prompt.trim().length > 0;
  const postSteps = [
    tpl.postUpscale ? 'Upscale 2×' : null,
    tpl.postRemoveBg ? 'Remove background' : null,
  ].filter((s): s is string => !!s);
  return (
    <HoverPopover
      interactive
      placement="top"
      width={320}
      content={
        <div>
          {hasSystem && (
            <>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: TM.textFaint,
                  letterSpacing: 0.4,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                }}
              >
                System prompt · {tpl.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: TM.text,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  marginBottom: 10,
                }}
              >
                {tpl.prompt}
              </div>
              <MagicBtn kind="secondary" size="sm" onClick={onResetToCustom}>
                Edit as plain prompt
              </MagicBtn>
              <div style={{ fontSize: 10, color: TM.textFaint, lineHeight: 1.4, marginTop: 6 }}>
                Moves this text into the prompt field and switches to No template.
              </div>
            </>
          )}
          {postSteps.length > 0 && (
            <div style={{ marginTop: hasSystem ? 12 : 0 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: TM.textFaint,
                  letterSpacing: 0.4,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                }}
              >
                After generation
              </div>
              <div style={{ fontSize: 12, color: TM.text, lineHeight: 1.5 }}>
                {postSteps.join(' → ')}
              </div>
              <div style={{ fontSize: 10, color: TM.textFaint, lineHeight: 1.4, marginTop: 6 }}>
                Runs on-device on the result automatically — free, no API key.
              </div>
            </div>
          )}
        </div>
      }
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: '1px 6px',
          borderRadius: 3,
          background: TM.blueFaint,
          color: TM.blue,
          letterSpacing: 0.3,
          textTransform: 'none',
          cursor: 'help',
        }}
      >
        {tpl.label}
      </span>
    </HoverPopover>
  );
};

// Center panel: the generated image (fills the area, zoom/pan/reset) or the
// empty / generating states, plus the prompt editor. The prompt field holds
// only the user's content — the active template's system instruction lives
// behind the badge next to the label.
export const CanvasPanel: React.FC<{
  generating: boolean;
  result: MagicResult | null;
  error: string | null;
  comingSoon: boolean;
  localOp: LocalOpState | null;
  primaryRef: MagicRef | null;
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  // Prompts this user has generated with before, most recent first — what the
  // field's Ctrl+Space autocomplete offers (see promptHistory.ts).
  promptHistory: string[];
  onGenerate: () => void;
  onResetTemplateToCustom: () => void;
  aspect: Aspect;
  activeTpl: string;
  templates: MagicTemplate[];
  // Key + model controls live in the prompt toolbar (moved out of the header).
  // Which pair is rendered depends on MAGIC_BACKEND_ENABLED; the unused branch
  // is a compile-time constant, so the bundler drops it.
  //
  // Direct path (legacy): the user's Gemini key in localStorage + the static
  // MODELS list.
  apiKey: string | null;
  onApiKeyChange: (key: string | null) => void;
  model: string;
  onPickModel: (id: string) => void;
  // Backend path: keys registered server-side + the (key, model) pairs they
  // offer.
  keys: AiApiKey[];
  keysLoading: boolean;
  keysError: string | null;
  onReloadKeys: () => void;
  onAddKey: (payload: CreateAiApiKeyPayload) => Promise<AiApiKey | null>;
  onRemoveKey: (id: number) => Promise<boolean>;
  modelOptions: ModelSelection[];
  selection: ModelSelection | null;
  onSelect: (selection: ModelSelection) => void;
  // Generate is unavailable while the keys are still loading, or (backend path)
  // while nothing is selected to generate with.
  generateDisabled: boolean;
}> = ({
  generating,
  result,
  error,
  comingSoon,
  localOp,
  primaryRef,
  prompt,
  setPrompt,
  promptHistory,
  onGenerate,
  onResetTemplateToCustom,
  aspect,
  activeTpl,
  templates,
  apiKey,
  onApiKeyChange,
  model,
  onPickModel,
  keys,
  keysLoading,
  keysError,
  onReloadKeys,
  onAddKey,
  onRemoveKey,
  modelOptions,
  selection,
  onSelect,
  generateDisabled,
}) => {
  const tpl = templates.find((t) => t.id === activeTpl);
  // Show the badge for modes that carry a system instruction or any
  // after-generation post-processing (so the user sees what will be applied).
  // "No template" with no post-processing shows nothing.
  const showBadge =
    !!tpl && (tpl.prompt.trim().length > 0 || !!tpl.postUpscale || !!tpl.postRemoveBg);

  // Auto-grow the prompt textarea with its content, between PROMPT_MIN_ROWS and
  // PROMPT_MAX_ROWS. Since the field sits at the bottom of the panel, growing
  // taller pushes its top edge upward.
  // Open state for the API-key popover, lifted so the "coming soon" screen's
  // "Connect API Key" button can open the same popover that lives in the prompt
  // toolbar below.
  const [apiKeyOpen, setApiKeyOpen] = useState(false);

  // Backend path with a loaded-but-empty key list: the user has nothing to
  // generate with, so lead with the connect screen instead of a "ready to
  // generate" canvas that would bounce off the gate. A *failed* load is a
  // different case (KeysErrorState) — an empty list there would be a lie.
  const needsKey = MAGIC_BACKEND_ENABLED && !keysLoading && !keysError && keys.length === 0;

  const promptRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    const minHeight = PROMPT_LINE_HEIGHT * PROMPT_MIN_ROWS + PROMPT_VERTICAL_PADDING;
    const maxHeight = PROMPT_LINE_HEIGHT * PROMPT_MAX_ROWS + PROMPT_VERTICAL_PADDING;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [prompt]);

  // ─── Past-prompt autocomplete ──────────────────────────────────────
  // Opened explicitly (hotkey or the hint above the field) rather than while
  // typing, so writing a fresh prompt is never interrupted by a popover.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestIndex, setSuggestIndex] = useState(0);
  const suggestions = useMemo(
    () => (suggestOpen ? suggestPrompts(promptHistory, prompt) : []),
    [suggestOpen, promptHistory, prompt],
  );
  // A user with no past prompts has no popover at all; one whose current text
  // matches none of them still gets it, saying so — a hotkey that looks broken
  // is worse than an empty answer.
  const suggestVisible = suggestOpen && promptHistory.length > 0;
  const activeIndex = Math.min(suggestIndex, Math.max(suggestions.length - 1, 0));

  // Editing re-filters the list, so the highlight goes back to the best match.
  useEffect(() => setSuggestIndex(0), [prompt]);

  const openSuggest = () => {
    setSuggestIndex(0);
    setSuggestOpen(true);
    promptRef.current?.focus();
  };

  const applySuggestion = (text: string) => {
    setPrompt(text);
    setSuggestOpen(false);
    const el = promptRef.current;
    if (!el) return;
    el.focus();
    // Caret after the inserted prompt, once React has flushed the new value.
    requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
  };

  const onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl/⌘+Enter generates whatever the autocomplete is doing.
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      setSuggestOpen(false);
      onGenerate();
      return;
    }
    // Ctrl+Space (Windows/Linux) or ⌥Space (macOS) toggles the autocomplete.
    // ⌥Space emits a non-breaking space on macOS, hence the physical-key check.
    const spacePressed = e.nativeEvent.code === 'Space' || e.key === ' ';
    if (spacePressed && (e.ctrlKey || e.altKey) && !e.metaKey) {
      e.preventDefault();
      if (suggestOpen) setSuggestOpen(false);
      else openSuggest();
      return;
    }
    if (!suggestVisible) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      // The studio's Modal closes on Escape — keep this one for the popover.
      e.stopPropagation();
      setSuggestOpen(false);
      return;
    }
    // The "no matches" state has nothing to navigate or insert; typing on.
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestIndex((activeIndex + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestIndex((activeIndex - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      applySuggestion(suggestions[activeIndex]);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        background: TM.bgSoft,
      }}
    >
      {localOp ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          <ProcessingState localOp={localOp} />
        </div>
      ) : result && !generating ? (
        <ResultViewer result={result} />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          {generating ? (
            <GeneratingState aspect={aspect} />
          ) : keysError ? (
            <KeysErrorState message={keysError} onRetry={onReloadKeys} />
          ) : comingSoon || needsKey ? (
            <ComingSoonState onConnectApiKey={() => setApiKeyOpen(true)} />
          ) : error ? (
            <ErrorState message={error} />
          ) : (
            <EmptyCanvas primaryRef={primaryRef} tpl={tpl} />
          )}
        </div>
      )}

      <div
        style={{
          padding: 14,
          borderTop: `1px solid ${TM.border}`,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: TM.textMute,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Prompt
          {tpl && showBadge && (
            <TemplateBadge tpl={tpl} onResetToCustom={onResetTemplateToCustom} />
          )}
          <div style={{ flex: 1 }} />
          {/* The autocomplete is hotkey-driven, so the combination has to be
              visible somewhere — and the hint doubles as a click target for
              users who'd rather not remember it. Hidden until there is at least
              one past prompt, so it never advertises an empty list. */}
          {promptHistory.length > 0 && (
            <button
              type="button"
              className="al-magic-prompt-hint"
              // Keeps the textarea's focus (and caret) while opening.
              onMouseDown={(e) => e.preventDefault()}
              onClick={openSuggest}
              title="Insert one of your past prompts"
            >
              <HistoryIcon size={11} />
              <kbd>{SUGGEST_HOTKEY_LABEL}</kbd>
              past prompts
            </button>
          )}
        </div>
        {/* Unified prompt frame (ChatGPT-style): the textarea fills the full
            width, with a toolbar row underneath — API key + model picker on the
            left, Generate on the right. The textarea drops its own border so the
            surrounding box carries the single frame and focus highlight. */}
        <div className="al-magic-prompt-box">
          {suggestVisible && (
            <PromptSuggest
              items={suggestions}
              activeIndex={activeIndex}
              query={prompt}
              onPick={applySuggestion}
              onHover={setSuggestIndex}
            />
          )}
          <textarea
            ref={promptRef}
            className="al-magic-prompt al-magic-prompt_seamless"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onPromptKeyDown}
            onBlur={() => setSuggestOpen(false)}
            rows={PROMPT_MIN_ROWS}
            placeholder={
              showBadge
                ? 'Describe the specifics — the template adds the rest. Ctrl+Enter to generate.'
                : 'Describe what you want. References handle the style. Ctrl+Enter to generate.'
            }
          />
          <div className="al-magic-prompt-box__toolbar">
            {MAGIC_BACKEND_ENABLED ? (
              <>
                <AiKeysControl
                  keys={keys}
                  loading={keysLoading}
                  error={keysError}
                  onReload={onReloadKeys}
                  onAddKey={onAddKey}
                  onRemoveKey={onRemoveKey}
                  compact
                  placement="top-left"
                  open={apiKeyOpen}
                  onOpenChange={setApiKeyOpen}
                />
                <ModelPicker
                  options={modelOptions}
                  value={selection}
                  onPick={onSelect}
                  keys={keys}
                  onAddKey={() => setApiKeyOpen(true)}
                  placement="top-left"
                />
              </>
            ) : (
              <>
                <ApiKeyControl
                  apiKey={apiKey}
                  onChange={onApiKeyChange}
                  compact
                  placement="top-left"
                  open={apiKeyOpen}
                  onOpenChange={setApiKeyOpen}
                />
                <LegacyModelPicker modelId={model} onPick={onPickModel} placement="top-left" />
              </>
            )}
            <div style={{ flex: 1 }} />
            <MagicBtn
              kind="magic"
              icon={<MagicIcon size={14} />}
              disabled={generateDisabled}
              onClick={onGenerate}
            >
              Generate
            </MagicBtn>
          </div>
        </div>
      </div>
    </div>
  );
};
